import type { CarrotPlanFormStep, UnslottedStep } from "../CarrotPlanFormTypes"

export const TOTAL_STEP_SLOTS = 5

/**
 * Arranges step rows into the form's fixed slots with prioritized items pinned to the top
 * and required items pinned to the bottom.
 *
 * Layout: prioritized at the top, user-added next, empty slots in the middle, required at the bottom.
 * Overflow: prioritized takes precedence over required; user-added is sliced to fit remaining slots.
 * `id` and `stepIndex` are stamped here based on each row's final slot position.
 */
export function arrangeStepRows(
  userAddedSteps: UnslottedStep[],
  requiredSteps: UnslottedStep[],
  prioritizedSteps: UnslottedStep[] = []
): CarrotPlanFormStep[] {
  const cappedPrioritized = prioritizedSteps.slice(0, TOTAL_STEP_SLOTS)
  const cappedRequired = requiredSteps.slice(0, Math.max(0, TOTAL_STEP_SLOTS - cappedPrioritized.length))
  const userSlotsAvailable = Math.max(0, TOTAL_STEP_SLOTS - cappedRequired.length - cappedPrioritized.length)
  const cappedUserAdded = userAddedSteps.slice(0, userSlotsAvailable)
  const emptyCount = TOTAL_STEP_SLOTS - cappedPrioritized.length - cappedUserAdded.length - cappedRequired.length
  const emptySlots: UnslottedStep[] = Array.from({ length: emptyCount }, () => ({}))

  return [...cappedPrioritized, ...cappedUserAdded, ...emptySlots, ...cappedRequired].map((step, i) => ({
    ...step,
    id: `stepId-${i}`,
    stepIndex: i
  }))
}

/**
 * Lays out backend-slotted recommendations into the form's fixed slots,
 * preserving the order the recommendations engine returned. Pads with empties
 * at the end if fewer than TOTAL_STEP_SLOTS were returned. `id` and `stepIndex`
 * are stamped here based on slot position.
 *
 * Used for the gate-on (recommendations) path. The gate-off (legacy required-
 * items) path uses `arrangeStepRows` instead, which computes layout itself.
 */
export function slotRecommendedSteps(steps: UnslottedStep[]): CarrotPlanFormStep[] {
  const capped = steps.slice(0, TOTAL_STEP_SLOTS)
  const emptyCount = TOTAL_STEP_SLOTS - capped.length
  const empties: UnslottedStep[] = Array.from({ length: emptyCount }, () => ({}))

  return [...capped, ...empties].map((step, i) => ({
    ...step,
    id: `stepId-${i}`,
    stepIndex: i
  }))
}
